import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, Send, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ChatPage() {
  const { axiosAuth } = useAuth();
  const { settings } = useSettings();
  const accent = settings?.primary_color || "#DC2626";
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: text }]);
    setLoading(true);
    try {
      const api = axiosAuth();
      const res = await api.post("/chat", { message: text, session_id: sessionId });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (err) {
      const detail = err.response?.data?.detail || "Errore di comunicazione";
      setMessages(prev => [...prev, { role: "assistant", content: `Errore: ${detail}` }]);
    } finally { setLoading(false); }
  };

  const clearChat = async () => {
    try {
      const api = axiosAuth();
      await api.delete(`/chat/${sessionId}`);
      setMessages([]);
      toast.success("Chat cancellata");
    } catch {}
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]" data-testid="chat-page">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-slate-200 dark:border-slate-700 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: accent }}>
            <MessageCircle className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-white">Assistente RG Rent</h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">Chiedimi disponibilita, fatturato, info clienti...</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={clearChat} className="rounded-lg text-xs" data-testid="chat-clear-button">
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Cancella Chat
        </Button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 pr-2">
        {messages.length === 0 && (
          <div className="text-center py-16">
            <MessageCircle className="w-14 h-14 mx-auto mb-4 text-slate-200 dark:text-slate-700" />
            <p className="text-base text-slate-400 dark:text-slate-500">Ciao! Sono l'assistente RG Rent.</p>
            <p className="text-sm text-slate-300 dark:text-slate-600 mt-1">Chiedimi disponibilita, fatturato, info clienti...</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[70%] px-4 py-3 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${
                m.role === "user"
                  ? "text-white rounded-br-md"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-bl-md"
              }`}
              style={m.role === "user" ? { backgroundColor: accent } : {}}
              data-testid={`chat-message-${i}`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-100 dark:bg-slate-800 px-4 py-3 rounded-2xl rounded-bl-md">
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="pt-4 border-t border-slate-200 dark:border-slate-700 mt-4">
        <div className="flex items-center gap-3">
          <Input
            ref={inputRef}
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Scrivi un messaggio..."
            className="flex-1 h-12 rounded-xl text-sm"
            disabled={loading}
          />
          <Button
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            size="icon"
            className="h-12 w-12 rounded-xl text-white shrink-0"
            style={{ backgroundColor: accent }}
            data-testid="chat-send-button"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
